/**
 * Snooze helpers (spec §15). The server marks a snoozed message with the keyword
 * `TIK_SNZ_<minutes since epoch>` and moves it to the `Snoozed` folder.
 */
import type { I18nKey } from '$lib/i18n';
import type { Lang } from '$lib/i18n/lang.svelte';

export const SNOOZE_PREFIX = 'TIK_SNZ_';
/** The snoozed folder has no special-use flag; the app matches this literal path. */
export const SNOOZED_FOLDER = 'Snoozed';

export interface SnoozePreset {
  key: I18nKey;
  at: number;
}

const HOUR = 3600e3;

function atHour(base: Date, plusDays: number, hour: number): number {
  const d = new Date(base);
  d.setDate(d.getDate() + plusDays);
  d.setHours(hour, 0, 0, 0);
  return d.getTime();
}

/** `snoozeLater` +3 h · `snoozeTomorrow` tomorrow 08:00 · `snoozeWeek` +7 d 08:00. */
export function snoozePresets(now: Date = new Date()): SnoozePreset[] {
  return [
    { key: 'snoozeLater', at: now.getTime() + 3 * HOUR },
    { key: 'snoozeTomorrow', at: atHour(now, 1, 8) },
    { key: 'snoozeWeek', at: atHour(now, 7, 8) },
  ];
}

/** Scheduled-send presets: same times, `sched*` labels. */
export function schedulePresets(now: Date = new Date()): SnoozePreset[] {
  return [
    { key: 'schedLater', at: now.getTime() + 3 * HOUR },
    { key: 'schedTomorrow', at: atHour(now, 1, 8) },
    { key: 'schedWeek', at: atHour(now, 7, 8) },
  ];
}

/** Wake time (epoch ms) encoded in a row's flags, or null. */
export function snoozeUntil(flags: readonly string[] | null | undefined): number | null {
  for (const f of flags ?? []) {
    if (!f.startsWith(SNOOZE_PREFIX)) continue;
    const min = Number(f.slice(SNOOZE_PREFIX.length));
    if (Number.isFinite(min) && min > 0) return min * 60e3;
  }
  return null;
}

/** Build the keyword for a wake time (mirrors the server's `floor(until/60000)`). */
export function snoozeFlag(untilMs: number): string {
  return SNOOZE_PREFIX + String(Math.floor(untilMs / 60e3));
}

/** Remaining-time chip: ar `تعود بعد N د/س/يوم`, en `in Nm/Nh/Nd`. */
export function snoozeChipText(untilMs: number, lang: Lang, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((untilMs - now) / 60e3));
  const ar = lang === 'ar';
  if (minutes < 60) return ar ? `تعود بعد ${minutes} د` : `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return ar ? `تعود بعد ${hours} س` : `in ${hours}h`;
  const days = Math.round(hours / 24);
  return ar ? `تعود بعد ${days} يوم` : `in ${days}d`;
}

/** Custom picker: 14 day chips starting today. */
export function pickerDays(now: Date = new Date(), count = 14): Date[] {
  const out: Date[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + i);
    out.push(d);
  }
  return out;
}

/** Picker time chips (hours). */
export const PICKER_HOURS: readonly number[] = Object.freeze([8, 12, 17, 20]);

/**
 * Time chips available for a picked day: on today only times more than 5 minutes in the future
 * remain (spec §15 `snoozeNoTime` when none is left).
 */
export function pickerTimes(day: Date, now: Date = new Date()): number[] {
  const isToday = day.toDateString() === now.toDateString();
  if (!isToday) return [...PICKER_HOURS];
  return PICKER_HOURS.filter((h) => {
    const d = new Date(day);
    d.setHours(h, 0, 0, 0);
    return d.getTime() > now.getTime() + 5 * 60e3;
  });
}

/** Combine a picked day and hour into an epoch timestamp. */
export function pickerAt(day: Date, hour: number): number {
  const d = new Date(day);
  d.setHours(hour, 0, 0, 0);
  return d.getTime();
}

/** Whether a folder path is the snoozed folder (the literal comparison the app uses). */
export function isSnoozedFolder(path: string | null | undefined): boolean {
  return path === SNOOZED_FOLDER;
}
